import React from 'react';
import { motion } from 'framer-motion';
interface VerbTableProps {
  verb: string;
  tense: 'present' | 'past' | 'perfect';
  persons: string[];
  forms: string[];
  highlight?: string;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * VerbTable - Dutch verb conjugation table visual
 *
 * Shows verb conjugation patterns with educational explanations.
 * Used for SWW (Spelling Werkwoorden) module questions.
 *
 * stimulus mode: compact — verb info + conjugation table only
 * support mode: full educational panel with explanations and tips
 */
export function VerbTable({
  verb,
  tense,
  persons,
  forms,
  highlight,
  label,
  mode = 'support'
}: VerbTableProps) {
  // Extract stem (remove -en ending)
  const stem = verb.endsWith('en') ? verb.slice(0, -2) : verb;
  // Tense labels
  const tenseLabels = {
    present: 'Tegenwoordige Tijd',
    past: 'Verleden Tijd',
    perfect: 'Voltooid Deelwoord'
  };
  // ── STIMULUS MODE: compact — verb + table only ──────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-14008">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-200" data-id="element-14009">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-14010">
              <span className="text-xs font-bold text-indigo-800" data-id="element-14011">{label}</span>
            </div>}

          {/* Verb Info - compact */}
          <div className="mb-3 flex items-center justify-center gap-3" data-id="element-14012">
            <div className="text-center" data-id="element-14013">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-14014">
                Werkwoord
              </div>
              <div className="text-lg font-black text-indigo-700" data-id="element-14015">{verb}</div>
            </div>
            <div className="text-lg text-slate-300" data-id="element-14016">→</div>
            <div className="text-center" data-id="element-14017">
              <div className="text-[10px] font-bold text-slate-500 uppercase" data-id="element-14018">
                Stam
              </div>
              <div className="text-lg font-black text-purple-700" data-id="element-14019">{stem}</div>
            </div>
          </div>

          {/* Conjugation Table - compact */}
          <div className="bg-white/90 rounded-lg overflow-hidden border border-indigo-200" data-id="element-14020">
            <table className="w-full text-sm" data-id="element-14021">
              <thead data-id="element-14022">
                <tr className="bg-indigo-100/70" data-id="element-14023">
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-14024">
                    Persoon
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-indigo-900" data-id="element-14025">
                    Vorm
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-14026">
                {persons.map((person, index) => {
                const form = forms[index];
                const isHighlighted = highlight === person || highlight === form;
                return <motion.tr key={person} initial={{
                  opacity: 0,
                  x: -5
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.06
                }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : ''}`} data-id="element-14027">
                      <td className="px-3 py-1.5" data-id="element-14028">
                        <span className={`font-semibold text-xs ${isHighlighted ? 'text-indigo-700' : 'text-slate-600'}`} data-id="element-14029">
                          {person}
                        </span>
                      </td>
                      <td className="px-3 py-1.5" data-id="element-14030">
                        <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-14031">
                          {form}
                        </span>
                      </td>
                    </motion.tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-14032">
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 md:p-8 border-2 border-indigo-200 shadow-lg" data-id="element-14033">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-14034">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-indigo-200 shadow-sm" data-id="element-14035">
            <span className="text-2xl" data-id="element-14036">📝</span>
            <div className="text-left" data-id="element-14037">
              <div className="text-sm font-bold text-indigo-900" data-id="element-14038">
                {label || 'Werkwoord Vervoegen'}
              </div>
              <div className="text-xs text-indigo-600 font-semibold" data-id="element-14039">
                {tenseLabels[tense]}
              </div>
            </div>
          </div>
        </div>

        {/* Verb Info */}
        <div className="mb-6 bg-white/80 rounded-xl p-4 border border-indigo-100" data-id="element-14040">
          <div className="flex items-center justify-center gap-4 flex-wrap" data-id="element-14041">
            <div className="text-center" data-id="element-14042">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-14043">
                Werkwoord
              </div>
              <div className="text-2xl font-black text-indigo-700" data-id="element-14044">{verb}</div>
            </div>
            <div className="text-2xl text-slate-300" data-id="element-14045">→</div>
            <div className="text-center" data-id="element-14046">
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1" data-id="element-14047">
                Stam
              </div>
              <div className="text-2xl font-black text-purple-700" data-id="element-14048">{stem}</div>
            </div>
          </div>
        </div>

        {/* Conjugation Table */}
        <div className="bg-white/90 rounded-xl overflow-hidden border border-indigo-200 shadow-sm mb-6" data-id="element-14049">
          <table className="w-full" data-id="element-14050">
            <thead data-id="element-14051">
              <tr className="bg-indigo-100 border-b border-indigo-200" data-id="element-14052">
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-14053">
                  Persoon
                </th>
                <th className="px-4 py-3 text-left text-sm font-bold text-indigo-900" data-id="element-14054">
                  Vorm
                </th>
              </tr>
            </thead>
            <tbody data-id="element-14055">
              {persons.map((person, index) => {
              const form = forms[index];
              const isHighlighted = highlight === person || highlight === form;
              return <motion.tr key={person} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className={`border-b border-slate-100 last:border-b-0 ${isHighlighted ? 'bg-indigo-50' : 'hover:bg-slate-50'}`} data-id="element-14056">
                    <td className="px-4 py-3" data-id="element-14057">
                      <span className={`font-bold ${isHighlighted ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-14058">
                        {person}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-14059">
                      <span className={`font-bold text-lg ${isHighlighted ? 'text-indigo-700' : 'text-slate-800'}`} data-id="element-14060">
                        {form}
                      </span>
                    </td>
                  </motion.tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-14061">
          {/* How to find the stem */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-14062">
            <div className="flex items-start gap-3" data-id="element-14063">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-14064">
                <span className="text-blue-600 text-lg" data-id="element-14065">🔍</span>
              </div>
              <div data-id="element-14066">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-14067">
                  Hoe vind je de stam?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14068">
                  De <strong data-id="element-14069">stam</strong> krijg je door <strong data-id="element-14070">-en</strong>{' '}
                  weg te halen van het hele werkwoord.{' '}
                  <span className="font-mono bg-blue-50 px-2 py-0.5 rounded border border-blue-200" data-id="element-14071">
                    {verb}
                  </span>{' '}
                  →{' '}
                  <span className="font-mono bg-purple-50 px-2 py-0.5 rounded border border-purple-200" data-id="element-14072">
                    {stem}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Conjugation pattern */}
          {tense === 'present' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14073">
              <div className="flex items-start gap-3" data-id="element-14074">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14075">
                  <span className="text-emerald-600 text-lg" data-id="element-14076">📋</span>
                </div>
                <div data-id="element-14077">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14078">
                    Patroon Tegenwoordige Tijd
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14079">
                    <p data-id="element-14080">
                      <strong data-id="element-14081">ik:</strong> stam (geen -t) →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14082">
                        {stem}
                      </span>
                    </p>
                    <p data-id="element-14083">
                      <strong data-id="element-14084">jij/je, u:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14085">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-14086">
                      <strong data-id="element-14087">hij/zij/het:</strong> stam + t →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14088">
                        {stem}t
                      </span>
                    </p>
                    <p data-id="element-14089">
                      <strong data-id="element-14090">wij/jullie/zij:</strong> hele werkwoord →{' '}
                      <span className="font-semibold text-emerald-700" data-id="element-14091">
                        {verb}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'past' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14092">
              <div className="flex items-start gap-3" data-id="element-14093">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14094">
                  <span className="text-emerald-600 text-lg" data-id="element-14095">📋</span>
                </div>
                <div data-id="element-14096">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14097">
                    Patroon Verleden Tijd (Zwakke werkwoorden)
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14098">
                    <p data-id="element-14099">
                      <strong data-id="element-14100">enkelvoud:</strong> stam + te / de
                    </p>
                    <p data-id="element-14101">
                      <strong data-id="element-14102">meervoud:</strong> stam + ten / den
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-14103">
                      <em data-id="element-14104">
                        Tip: Gebruik 't kofschip om te bepalen of het -te of -de
                        is!
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {tense === 'perfect' && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-14105">
              <div className="flex items-start gap-3" data-id="element-14106">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-14107">
                  <span className="text-emerald-600 text-lg" data-id="element-14108">📋</span>
                </div>
                <div data-id="element-14109">
                  <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-14110">
                    Patroon Voltooid Deelwoord
                  </h4>
                  <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-14111">
                    <p data-id="element-14112">
                      <strong data-id="element-14113">Zwakke werkwoorden:</strong> ge + stam + t / d
                    </p>
                    <p data-id="element-14114">
                      <strong data-id="element-14115">Sterke werkwoorden:</strong> ge + ... + en
                    </p>
                    <p className="mt-2 text-xs text-emerald-800 bg-emerald-100/50 p-2 rounded" data-id="element-14116">
                      <em data-id="element-14117">
                        Tip: Maak het woord langer om te horen of het op een -t
                        of -d eindigt (bijv. gemaakte → gemaakt).
                      </em>
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-14118">
            <div className="flex items-start gap-3" data-id="element-14119">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-14120">
                <span className="text-amber-600 text-lg" data-id="element-14121">💡</span>
              </div>
              <div data-id="element-14122">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-14123">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14124">
                  Bij <strong data-id="element-14125">inversie</strong> (als 'jij' achter het werkwoord
                  staat) valt de -t weg: <em data-id="element-14126">jij loopt</em> wordt{' '}
                  <em data-id="element-14127">loop jij?</em>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}